import { DownloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { type Dispatch, type SetStateAction, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ValidateImageMetadataFileMutationResult } from 'types/atlas-graphql';

import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { Steps } from '../UploadInspectionDataModal';
import { AdditionalInfoText, ButtonContainer } from '../UploadInspectionDataModal.style';

export type ImageDetailsValidationProps = ValidateImageMetadataFileMutationResult['data'] & {
  fileName: string;
  setStep: Dispatch<SetStateAction<Steps>>;
  setErroredUploadSteps: Dispatch<
    SetStateAction<{
      summary: boolean;
      imageDetails: boolean;
    }>
  >;
};

export const Validation: React.FC<ImageDetailsValidationProps> = ({
  validateImageMetadataFile,
  fileName,
  setStep,
  setErroredUploadSteps,
}) => {
  const { t } = useTranslation();
  const { setAlertState } = useUploadInspectionDataModalContext();

  useEffect(() => {
    if (validateImageMetadataFile) {
      setAlertState({
        alertTitle: `${failureCount} row${failureCount === 1 ? '' : 's'} in ${fileName} contained invalid or incomplete data.`,
        alertType: 'error',
        alertDescription:
          'Please use the button below to download the error file. Then complete the missing data and upload again.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateImageMetadataFile]);

  if (!validateImageMetadataFile) {
    return null;
  }
  const { draftImages, errorFileUrl, failureCount } = validateImageMetadataFile;

  const handleClickReupload = () => {
    setAlertState(null);
    setStep(Steps.DetailsUpload);
    setErroredUploadSteps((uploadErrors) => ({ ...uploadErrors, imageDetails: false }));
  };

  return (
    <>
      <p>
        {t('inspection.imageDetailMappingsValidated', {
          count: draftImages?.length ?? 0,
          defaultValue_one: '{{count}} image detail mapping validated.',
          defaultValue_other: '{{count}} image detail mappings validated.',
        })}
      </p>
      <Button
        data-testid="download-error-file-btn"
        icon={<DownloadOutlined />}
        href={errorFileUrl ?? undefined}
      >
        {t('assets.downloadErrorFile', 'Download Error File')}
      </Button>
      <AdditionalInfoText>
        <Trans i18nKey="inspection.imageDetailsFormattingNote">
          Note: Horizon requires the image details to be formatted in a way that is compatible with
          our system. For more information on configured inspection details uploads, please visit
          our{' '}
          <a
            href="https://guide.skyspecs.com/horizon/-MPobGVTLxgvA9kxtck7/"
            target="_blank"
            rel="noopener noreferrer"
          >
            User Guide
          </a>
          .
        </Trans>
      </AdditionalInfoText>
      <ButtonContainer>
        <Button type="primary" onClick={handleClickReupload}>
          {t('components.reupload', 'Reupload')}
        </Button>
      </ButtonContainer>
    </>
  );
};
